import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, Phone, ArrowRight, MessageCircle } from 'lucide-react';
import { services, brand } from '../data/mock';
import Logo from './Logo';

const navLinks = [
  { name: 'Home', to: '/' },
  { name: 'About', to: '/about' },
  { name: 'Services', to: '/services', dropdown: true },
  { name: 'Industries', to: '/#industries', hash: true },
  { name: 'Why Durga', to: '/why-durga' },
  { name: 'Career', to: '/career' },
  { name: 'Contact', to: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);
  const [openServices, setOpenServices] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpenMobile(false);
    setOpenServices(false);
  }, [location.pathname]);

  return (
    <>
      {/* Top strip */}
      <div className="hidden lg:block bg-slate-900 text-white/85 text-xs">
        <div className="max-w-[1400px] mx-auto px-8 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`tel:${brand.phone}`} className="flex items-center gap-1.5 hover:text-[#F5B800] transition-colors">
              <Phone size={12} /> {brand.phone}
            </a>
            <a href={`mailto:${brand.email}`} className="hover:text-[#F5B800] transition-colors">
              {brand.email}
            </a>
            <span className="text-white/40">·</span>
            <span className="text-white/60">Serving Karnataka · Goa · Maharashtra · Tamil Nadu</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#F5B800] font-semibold uppercase tracking-widest text-[10px]">ISO 9001 · ISO 14001 · ISO 45001 · PSARA · FSSAI</span>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_4px_rgba(15,23,42,0.08)] border-b border-slate-100'
            : 'bg-white border-b border-slate-100'
        }`}
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-8 h-[72px]">
          <Link to="/" className="shrink-0">
            <Logo variant="dark" size="lg" showName={false} />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => setOpenServices(true)}
                  onMouseLeave={() => setOpenServices(false)}
                >
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `flex items-center gap-1 px-4 py-2 text-[14px] font-medium transition-colors ${
                        isActive ? 'text-[#0A2540]' : 'text-slate-700 hover:text-[#0A2540]'
                      }`
                    }
                  >
                    {link.name}
                    <ChevronDown size={14} className={`transition-transform ${openServices ? 'rotate-180' : ''}`} />
                  </NavLink>
                  {openServices && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                      <div className="w-[720px] bg-white shadow-2xl rounded-2xl border border-slate-100 overflow-hidden ring-1 ring-slate-900/5">
                        <div className="grid grid-cols-2">
                          <div className="p-6 bg-slate-50 border-r border-slate-100">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-[#0A2540] mb-3">Our Services</div>
                            <h4 className="text-xl font-bold leading-tight mb-2 text-slate-900">One partner for every service.</h4>
                            <p className="text-slate-600 text-sm leading-relaxed">From mechanised housekeeping to drone facade cleaning &mdash; pick a single service or bundle everything.</p>
                            <Link to="/services" className="mt-6 inline-flex items-center gap-1.5 text-[#0A2540] hover:text-[#1E40AF] text-sm font-semibold transition-colors">
                              View all services <ArrowRight size={14} />
                            </Link>
                          </div>
                          <div className="p-3 max-h-[420px] overflow-y-auto">
                            {services.map((s) => (
                              <Link
                                key={s.slug}
                                to={`/services/${s.slug}`}
                                className="group flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors"
                              >
                                <div className="w-8 h-8 rounded-lg bg-slate-100 text-[#0A2540] flex items-center justify-center shrink-0 group-hover:bg-[#F5B800] transition-colors">
                                  <span className="text-[10px] font-bold">{s.title.substring(0, 2).toUpperCase()}</span>
                                </div>
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-slate-800 group-hover:text-[#0A2540] transition-colors flex items-center gap-1.5">
                                    {s.title}
                                    {s.isNew && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#F5B800]/20 text-[#0A2540] uppercase tracking-wider">New</span>}
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={link.name}
                  to={link.to}
                  className={({ isActive }) =>
                    `px-4 py-2 text-[14px] font-medium transition-colors ${
                      isActive ? 'text-[#0A2540]' : 'text-slate-700 hover:text-[#0A2540]'
                    }`
                  }
                  end={link.to === '/'}
                >
                  {link.name}
                </NavLink>
              )
            )}
          </nav>

          {/* CTAs */}
          <div className="hidden lg:flex items-center gap-2">
            <a
              href={`https://wa.me/${brand.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-medium text-slate-700 hover:text-[#25D366] hover:bg-slate-50 transition-colors"
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
            <Link
              to="/contact"
              className="btn-cta inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-[14px] font-semibold"
            >
              Request a Quote <ArrowRight size={14} />
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setOpenMobile(!openMobile)}
            className="lg:hidden p-2 text-slate-800"
            aria-label="Toggle menu"
          >
            {openMobile ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {openMobile && (
          <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col py-2">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.dropdown ? (
                    <>
                      <button onClick={() => setOpenServices(!openServices)} className="w-full flex items-center justify-between px-6 py-3 text-[15px] font-medium text-slate-800 border-b border-slate-100">
                        {link.name}
                        <ChevronDown size={16} className={`transition-transform ${openServices ? 'rotate-180' : ''}`} />
                      </button>
                      {openServices && (
                        <div className="bg-slate-50">
                          {services.map((s) => (
                            <Link key={s.slug} to={`/services/${s.slug}`} className="block pl-10 pr-6 py-2.5 text-sm text-slate-700 border-b border-slate-100">
                              {s.title} {s.isNew && <span className="ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#F5B800]/20 text-[#0A2540] uppercase">New</span>}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <NavLink to={link.to} className={({ isActive }) => `block px-6 py-3 text-[15px] font-medium border-b border-slate-100 ${isActive ? 'text-[#0A2540]' : 'text-slate-800'}`} end={link.to === '/'}>
                      {link.name}
                    </NavLink>
                  )}
                </div>
              ))}
              <div className="p-6 flex flex-col gap-2">
                <Link to="/contact" className="btn-cta text-center py-3 rounded-lg font-semibold">Request a Quote</Link>
                <a href={`https://wa.me/${brand.whatsapp}`} target="_blank" rel="noreferrer" className="bg-[#25D366] text-white text-center py-3 rounded-lg font-semibold flex items-center justify-center gap-2">
                  <MessageCircle size={16} /> Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
